import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Paper, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  TextField, Snackbar, Alert, CircularProgress, Tooltip, FormControlLabel, Switch
} from '@mui/material';
import TitleBar from '../../components/TitleBar/TitleBar';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import { useNavigate } from 'react-router-dom';
import {
  getAllMetricLevels,
  createMetricLevel,
  updateMetricLevel,
  deleteMetricLevel,
  MetricLevel
} from '../../services/metricLevelService';

const emptyMetricLevel = (): MetricLevel => ({
  id: 0,
  name: '',
  display_label: '',
  is_period_based: false,
  description: ''
});

const MetricLevelSetup: React.FC = () => {
  const navigate = useNavigate();
  const [metricLevels, setMetricLevels] = useState<MetricLevel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<string>('');

  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<'edit' | 'add'>('edit');
  const [currentLevel, setCurrentLevel] = useState<MetricLevel>(emptyMetricLevel());

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    fetchMetricLevels();
  }, []);

  const fetchMetricLevels = async () => {
    setLoading(true);
    try {
      const data = await getAllMetricLevels();
      setMetricLevels(data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch metric levels');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteDialog = (name: string) => {
    setDeleteTarget(name);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeleteTarget('');
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteMetricLevel(deleteTarget);
      setSnackbar({
        open: true,
        message: `Metric level "${deleteTarget}" deleted successfully`,
        severity: 'success'
      });
      fetchMetricLevels();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || 'Unknown error occurred';
      setSnackbar({
        open: true,
        message: `Failed to delete metric level: ${errorMessage}`,
        severity: 'error'
      });
    } finally {
      handleCloseDeleteDialog();
    }
  };

  const handleOpenEditDialog = (level: MetricLevel) => {
    setEditMode('edit');
    setCurrentLevel({ ...level, description: level.description || '' });
    setOpenEditDialog(true);
  };

  const handleOpenAddDialog = () => {
    setEditMode('add');
    setCurrentLevel(emptyMetricLevel());
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setCurrentLevel(prev => ({
      ...prev,
      [name]: name === 'name' && editMode === 'add' ? value.toUpperCase() : value
    }));
  };

  const handlePeriodBasedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentLevel(prev => ({
      ...prev,
      is_period_based: e.target.checked
    }));
  };

  const handleSubmitLevel = async () => {
    try {
      const payload = {
        name: currentLevel.name.trim().toUpperCase(),
        display_label: currentLevel.display_label.trim(),
        is_period_based: !!currentLevel.is_period_based,
        description: (currentLevel.description || '').trim() || null
      };
      if (editMode === 'add') {
        await createMetricLevel(payload);
        setSnackbar({
          open: true,
          message: `Metric level "${payload.name}" created successfully`,
          severity: 'success'
        });
      } else {
        await updateMetricLevel(payload);
        setSnackbar({
          open: true,
          message: `Metric level "${payload.name}" updated successfully`,
          severity: 'success'
        });
      }
      fetchMetricLevels();
      handleCloseEditDialog();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || 'Unknown error occurred';
      setSnackbar({
        open: true,
        message: `Failed to ${editMode === 'add' ? 'create' : 'update'} metric level: ${errorMessage}`,
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const isFormValid = () => {
    return (
      currentLevel.name.trim() !== '' &&
      currentLevel.display_label.trim() !== ''
    );
  };

  const truncateDescription = (description?: string | null) => {
    if (!description) return '';
    const words = description.split(' ');
    return words.slice(0, 6).join(' ');
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <TitleBar title="" />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={() => navigate('/organization-setup')}
              sx={{
                border: '1px solid #ccc',
                borderRadius: 2,
                p: 1,
                backgroundColor: '#f5f5f5',
                '&:hover': {
                  backgroundColor: '#e0e0e0',
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight="bold">
              Metric Level Setup
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
          >
            Add Metric Level
          </Button>
        </Box>

        <Paper sx={{ p: 3, mb: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : metricLevels.length === 0 ? (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
              No metric levels found. Click the button above to add one.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small" sx={{ tableLayout: 'fixed', fontSize: '0.85rem' }}>
                <TableHead>
                  <TableRow>
                    <TableCell width="16%" sx={{ fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell width="18%" sx={{ fontWeight: 'bold' }}>Display Label</TableCell>
                    <TableCell width="14%" sx={{ fontWeight: 'bold' }}>Period-based</TableCell>
                    <TableCell width="32%" sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell width="20%" align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {metricLevels.map((level) => (
                    <TableRow key={level.name}>
                      <TableCell>{level.name}</TableCell>
                      <TableCell>{level.display_label}</TableCell>
                      <TableCell>{level.is_period_based ? 'Yes' : 'No'}</TableCell>
                      <TableCell title={level.description || ''}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {truncateDescription(level.description)}
                          {level.description && level.description.split(' ').length > 6 && (
                            <>
                              ...
                              <Tooltip title={level.description}>
                                <InfoIcon fontSize="small" sx={{ ml: 0.5 }} />
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <IconButton size="small" color="primary" onClick={() => handleOpenEditDialog(level)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleOpenDeleteDialog(level.name)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>

      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the metric level "{deleteTarget}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} fullWidth maxWidth="md">
        <DialogTitle>{editMode === 'edit' ? 'Edit' : 'Add'} Metric Level</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              name="name"
              label="Name"
              value={currentLevel.name}
              onChange={handleInputChange}
              fullWidth
              required
              disabled={editMode === 'edit'}
              helperText={
                editMode === 'edit'
                  ? 'Name cannot be modified'
                  : 'Unique uppercase code (e.g. SPRINT). PROJECT and AGGREGATE are reserved.'
              }
            />
            <TextField
              name="display_label"
              label="Display Label"
              value={currentLevel.display_label}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!currentLevel.is_period_based}
                  onChange={handlePeriodBasedChange}
                  color="primary"
                />
              }
              label="Period-based (has start/end dates on instances)"
            />
            <TextField
              name="description"
              label="Description"
              value={currentLevel.description || ''}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmitLevel}
            disabled={!isFormValid()}
            sx={{ textTransform: 'none' }}
          >
            {editMode === 'add' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MetricLevelSetup;
